import type { RawRow } from "../types";
import { cellText, isMeaningful, rowMap } from "./cell-utils";
import type { ParsedComponent, ParsedService, ParsedView } from "./types";

/**
 * 3 つの一覧シート (`サービスリスト` / `画面リスト` / `共通コンポーネントリスト`) を解析する。
 *
 * specId 欠落 / 重複は parser では検知せず、TargetBuilder 側で skip + warning を行う。
 */
export class ListSheetParser {
  parseService(rows: RawRow[]): ParsedService[] {
    if (rows.length === 0) return [];
    const { header, data } = this.extractRows(rows, SERVICE_HEADERS);
    return data.map(({ row, index }) => {
      const v = rowMap(header, row);
      return {
        specId: v["サービスID"] ?? "",
        name: v["サービス名"] ?? "",
        baseUrl: isMeaningful(v["domain"] ?? "") ? v["domain"] : undefined,
        description: v["概要"] ?? "",
        sourceRow: index,
      };
    });
  }

  parseView(rows: RawRow[]): ParsedView[] {
    if (rows.length === 0) return [];
    const { header, data } = this.extractRows(rows, VIEW_HEADERS);
    return data.map(({ row, index }) => {
      const v = rowMap(header, row);
      const paths = (v["path"] ?? "")
        .split(",")
        .map((p) => p.trim())
        .filter(isMeaningful);
      return {
        specId: v["画面ID"] ?? "",
        serviceSpecId: v["サービスID"] ?? "",
        name: v["画面名"] ?? "",
        paths,
        description: v["概要"] ?? "",
        sourceRow: index,
      };
    });
  }

  parseComponent(rows: RawRow[]): ParsedComponent[] {
    if (rows.length === 0) return [];
    const { header, data } = this.extractRows(rows, COMPONENT_HEADERS);
    return data.map(({ row, index }) => {
      const v = rowMap(header, row);
      return {
        specId: v["共通コンポーネントID"] ?? "",
        serviceSpecId: v["サービスID"] ?? "",
        name: v["共通コンポーネント名"] ?? "",
        description: v["概要"] ?? "",
        sourceRow: index,
      };
    });
  }

  /**
   * 期待カラム (`expectedHeaders`) が全て含まれる最初の行をヘッダーとして検出し、
   * 以降の非空行をデータとして返す。ヘッダーが見つからなければ空結果。
   */
  private extractRows(
    rows: RawRow[],
    expectedHeaders: readonly string[],
  ): { header: string[]; data: { row: RawRow; index: number }[] } {
    let headerIndex = -1;
    for (let i = 0; i < rows.length; i++) {
      const cells = new Set((rows[i] ?? []).map((c) => cellText(c)));
      if (expectedHeaders.every((h) => cells.has(h))) {
        headerIndex = i;
        break;
      }
    }
    if (headerIndex === -1) return { header: [], data: [] };

    const header = rows[headerIndex].map((c) => cellText(c));
    const data: { row: RawRow; index: number }[] = [];
    for (let i = headerIndex + 1; i < rows.length; i++) {
      const r = rows[i];
      if (!r || r.every((c) => cellText(c) === "")) continue;
      data.push({ row: r, index: i + 1 });
    }
    return { header, data };
  }
}

// 説明タブのカラム定義に準拠した期待ヘッダー (順序は問わない、すべて含まれていることが条件)
const SERVICE_HEADERS = ["サービスNo.", "サービス名", "サービスID", "domain", "概要"] as const;
const VIEW_HEADERS = ["画面No.", "サービス名", "サービスID", "画面名", "画面ID", "path", "概要"] as const;
const COMPONENT_HEADERS = [
  "共通コンポーネントNo.",
  "サービス名",
  "サービスID",
  "共通コンポーネント名",
  "共通コンポーネントID",
  "概要",
] as const;
